import { MutationCache, QueryClient } from "@tanstack/react-query";
import { createApiClient, type Transport } from "./api/client";

/** The mock backend is its own chunk, fetched only in mock mode, so a production build that
 * talks to the real API never downloads it. */
function lazyMockTransport(opts: { mode: Exclude<DataMode, "http">; reset?: boolean }): Transport {
  let ready: Promise<Transport> | null = null;
  return async (req) => {
    ready ??= import("./mock/transport").then((m) => m.createMockTransport(opts));
    return (await ready)(req);
  };
}
import type { ApiValue } from "./api/context";
import { endpoints } from "./api/endpoints";
import { isApiError } from "./api/errors";
import { isMockMode, type DataMode } from "./api/mode";
import { sessionStore } from "./auth/session";

export function createServices({ mode, reset, transport }: { mode: DataMode; reset?: boolean; transport?: Transport }) {
  // Every successful write is audited (§15.3), so any mutation also refreshes the platform audit log.
  const queryClient: QueryClient = new QueryClient({
    mutationCache: new MutationCache({ onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["platform-audit"] }) }),
    defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: (n, e) => !(isApiError(e) && e.status < 500) && n < 2 }, mutations: { retry: false } },
  });
  const t = transport ?? (isMockMode(mode) ? lazyMockTransport({ mode: mode as Exclude<DataMode, "http">, reset }) : undefined);
  const client = createApiClient({
    transport: t,
    getToken: sessionStore.token,
    onUnauthorized: () => {
      sessionStore.set(null);
      queryClient.clear();
    },
  });
  const api: ApiValue = { client, api: endpoints(client), mode };
  return { queryClient, api };
}
